/**
 * BottomTabBar — mobile-only navigation, fixed at the bottom of <AppShell>.
 *
 * 5 hardcoded NavLink tabs (Home / Study / Exam / Review / Progress).
 * Hidden on `md+` since the rail (or icon rail) takes over.
 * (Browse & Drills were dropped — those destinations are "coming soon"
 * and have no routes; see Sidebar.tsx.)
 */
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PATHS = {
  home: 'M3 11.5 12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1v-8.5Z',
  bookopen: 'M2 4h7a3 3 0 0 1 3 3v13a2 2 0 0 0-2-2H2V4ZM22 4h-7a3 3 0 0 0-3 3v13a2 2 0 0 1 2-2h8V4Z',
  rotate: 'M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5',
  filecheck: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6ZM14 3v6h6M9 15l2 2 4-4',
  barchart: 'M3 3v18h18M7 16v-7M12 16V8M17 16v-4',
} as const;

interface Tab {
  to: string;
  icon: keyof typeof PATHS;
  labelKey: string;
  end?: boolean;
}

const TABS: Tab[] = [
  /* nav.home ("Accueil"/"Home") — nav.dashboard wraps to 2 lines at 375px. */
  { to: '/dashboard', icon: 'home', labelKey: 'nav.home', end: true },
  { to: '/study', icon: 'bookopen', labelKey: 'nav.study' },
  { to: '/exam', icon: 'filecheck', labelKey: 'nav.exam' },
  { to: '/review', icon: 'rotate', labelKey: 'nav.review' },
  { to: '/profile', icon: 'barchart', labelKey: 'nav.progress' },
];

export function BottomTabBar() {
  const { t } = useTranslation();
  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-30 flex h-16 items-stretch border-t border-slate-200 bg-white/95 backdrop-blur dark:border-white/10 dark:bg-canvas-dark-2/95"
      aria-label={t('header.mobileNavigation')}
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {TABS.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) =>
            `flex flex-1 flex-col items-center justify-center gap-0.5 text-[10.5px] font-semibold transition-colors ${
              isActive
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
            }`
          }
        >
          {({ isActive }) => (
            <>
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={isActive ? 2.2 : 1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d={PATHS[tab.icon]} />
              </svg>
              <span className="max-w-full truncate px-0.5">{t(tab.labelKey)}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
